/**
 * 🏢 MULTI-TENANT SAAS: Token Health Manager
 *
 * Manages Auth0 token lifecycle with health detection and automatic recovery.
 * Prevents silent authentication failures and provides clear user feedback.
 *
 * Key Features:
 * - Token health monitoring
 * - Automatic recovery on token failure
 * - Graceful degradation for optional endpoints
 * - User-friendly error messages
 */

export enum TokenHealth {
  HEALTHY = 'healthy',           // Token is valid and working
  MISSING = 'missing',           // No token available
  EXPIRED = 'expired',           // Token exists but expired
  REFRESH_FAILED = 'refresh_failed',  // Cannot get new token
  NOT_CONFIGURED = 'not_configured'  // Auth0 not properly configured
}

export interface TokenHealthStatus {
  health: TokenHealth;
  hasAccessToken: boolean;
  hasRefreshToken: boolean;
  lastCheck: Date;
  error?: string;
}

/**
 * Centralized token management for SaaS application
 */
class TokenManager {
  private healthStatus: TokenHealthStatus | null = null;
  private healthCheckCallbacks: Set<(status: TokenHealthStatus) => void> = new Set();
  private getAccessTokenFn: (() => Promise<string>) | null = null;

  /**
   * Set the access token function (from Auth0 SDK)
   */
  setAccessTokenFunction(fn: () => Promise<string>) {
    this.getAccessTokenFn = fn;
  }

  /**
   * Subscribe to token health changes
   */
  onHealthChange(callback: (status: TokenHealthStatus) => void) {
    this.healthCheckCallbacks.add(callback);

    // Return unsubscribe function
    return () => {
      this.healthCheckCallbacks.delete(callback);
    };
  }

  /**
   * Get token with health detection and automatic recovery
   *
   * @param options - Token request options
   * @returns Access token or null if unavailable
   */
  async getToken(_options?: {
    silent?: boolean;
    skipHealthCheck?: boolean;
  }): Promise<string | null> {
    if (!this.getAccessTokenFn) {
      const status: TokenHealthStatus = {
        health: TokenHealth.NOT_CONFIGURED,
        hasAccessToken: false,
        hasRefreshToken: false,
        lastCheck: new Date(),
        error: 'Auth0 not configured - getAccessTokenFunction not set'
      };

      this.updateHealthStatus(status);
      return null;
    }

    try {
      const token = await this.getAccessTokenFn();

      // Token retrieved successfully
      const status: TokenHealthStatus = {
        health: TokenHealth.HEALTHY,
        hasAccessToken: !!token,
        hasRefreshToken: true, // If we got here, refresh token worked
        lastCheck: new Date(),
      };

      this.updateHealthStatus(status);
      return token;

    } catch (error: any) {
      // Token retrieval failed
      const isMissingRefresh = error?.message?.includes('Missing Refresh Token') ||
                               error?.message?.includes('login required');
      const isLoginRequired = error?.message?.includes('login required') ||
                              error?.error === 'login_required';

      let health: TokenHealth;
      let errorMessage = error?.message || 'Unknown error';

      if (isMissingRefresh || isLoginRequired) {
        // User needs to re-authenticate
        if (isLoginRequired) {
          health = TokenHealth.EXPIRED;
          errorMessage = 'Session expired - please log in again';
        } else {
          health = TokenHealth.MISSING;
          errorMessage = 'No active session - please log in';
        }
      } else if (error?.message?.includes('timeout') || error?.message?.includes('network')) {
        health = TokenHealth.REFRESH_FAILED;
        errorMessage = 'Network error - check your connection';
      } else {
        health = TokenHealth.REFRESH_FAILED;
      }

      const status: TokenHealthStatus = {
        health,
        hasAccessToken: false,
        hasRefreshToken: false,
        lastCheck: new Date(),
        error: errorMessage
      };

      this.updateHealthStatus(status);

      // Log user-friendly message
      console.warn(`[TokenManager] Authentication issue: ${errorMessage}`);

      return null;
    }
  }

  /**
   * Get current token health status
   */
  getHealthStatus(): TokenHealthStatus | null {
    return this.healthStatus;
  }

  /**
   * Check if user needs to re-authenticate
   */
  needsReauthentication(): boolean {
    if (!this.healthStatus) return false;

    return [
      TokenHealth.MISSING,
      TokenHealth.EXPIRED,
      TokenHealth.NOT_CONFIGURED,
    ].includes(this.healthStatus.health);
  }

  /**
   * Get user-friendly error message for current health status
   */
  getUserFriendlyMessage(): string | null {
    if (!this.healthStatus || this.healthStatus.health === TokenHealth.HEALTHY) {
      return null;
    }

    switch (this.healthStatus.health) {
      case TokenHealth.MISSING:
        return 'You need to log in to access this feature';
      case TokenHealth.EXPIRED:
        return 'Your session has expired. Please log in again.';
      case TokenHealth.REFRESH_FAILED:
        return 'Connection issue. Please refresh the page.';
      case TokenHealth.NOT_CONFIGURED:
        return 'Authentication is not configured properly. Please contact support.';
      default:
        return 'Authentication issue. Please try logging in again.';
    }
  }

  /**
   * Update health status and notify subscribers
   */
  private updateHealthStatus(status: TokenHealthStatus) {
    const healthChanged = !this.healthStatus ||
                          this.healthStatus.health !== status.health;

    this.healthStatus = status;

    if (healthChanged) {
      this.healthCheckCallbacks.forEach(callback => {
        try {
          callback(status);
        } catch (error) {
          console.error('[TokenManager] Health callback error:', error);
        }
      });
    }
  }

  /**
   * Reset health status (useful after logout)
   */
  reset() {
    this.healthStatus = null;
  }
}

// Singleton instance
export const tokenManager = new TokenManager();
